import { useContext } from "react";

import { Button } from "ui/button";

import CommentContext from "contexts/comment-context";

import { useCurrentUser } from "hooks/api/use-current-user";
import { hasPermission } from "utilities/user";
import { Permission } from "types/auth";
import { Comment } from "types/comments";

import Icons from "assets";

interface CommentActionsCardProps {
  isClientUser: boolean;
  comment: Comment;
  setReplyInputOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const CommentActionsCard = ({
  isClientUser,
  comment,
  setReplyInputOpen,
}: CommentActionsCardProps) => {
  const { updateComment } = useContext(CommentContext);

  const currentUser = useCurrentUser();
  const canComment = hasPermission(currentUser, Permission.PERM_COMMENT_WRITE);

  const handleReplyComment = () => {
    setReplyInputOpen((isOpen) => !isOpen);
  };

  const toggleFlagComment = () => {
    updateComment({ ...comment, actionItem: !comment.actionItem });
  };

  const toggleOpenComment = () => {
    updateComment({
      ...comment,
      open: !comment.open,
      actionItem: comment.open ? !comment.actionItem : false,
    });
  };

  const toggleHideComment = () => {
    updateComment({ ...comment, hidden: !comment.hidden });
  };

  const buttonStyles = {
    size: "small",
    variant: "text",
    className: "font-normal text-xs",
  } as const;

  return (
    <div>
      {/* Status: is comment visible to client? */}
      {!isClientUser && (
        <p className="flex items-center mb-1 text-xs gap-1">
          {comment.hidden ? (
            <>
              <Icons.EyeHidden className="size-4" />
              Hidden from client
            </>
          ) : (
            <>
              <Icons.EyeVisible className="size-4" />
              Visible to client
            </>
          )}
          {canComment && (
            <>
              <span>•</span>
              <Button
                data-testid="hidden-comment-trigger"
                onClick={toggleHideComment}
                {...buttonStyles}>
                {comment.hidden ? "Unhide" : "Hide"}
              </Button>
            </>
          )}
        </p>
      )}

      {/* Reply, Flag and Close Buttons */}
      {canComment && (
        <div className="flex gap-1">
          <Button data-testid="reply-comment-trigger" onClick={handleReplyComment} {...buttonStyles}>
            Reply
          </Button>
          <span>•</span>
          <Button
            data-testid="flag-comment-trigger"
            disabled={!comment.open}
            onClick={toggleFlagComment}
            {...buttonStyles}>
            {comment.actionItem ? "Unflag action item" : "Flag action item"}
          </Button>
          <span>•</span>
          <Button
            data-testid="open-comment-trigger"
            onClick={toggleOpenComment}
            disabled={isClientUser}
            {...buttonStyles}>
            {comment.open ? "Close" : "Open"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommentActionsCard;
